
import { useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { ExerciseImage } from "@/components/exercises/ExerciseImage";
import { isCardioExercise } from "@/components/workouts/exercise/utils/exerciseUtils";
import { useTranslations } from "@/hooks/useTranslations";

interface WorkoutCardProps {
  workout: any;
}

export const WorkoutCard = ({ workout }: WorkoutCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslations("workouts");
  
  // Agrupar exercícios por dia
  const exercisesByDay = workout.exercises?.reduce((acc: any, exercise: any) => {
    if (!acc[exercise.day]) {
      acc[exercise.day] = [];
    }
    acc[exercise.day].push(exercise);
    return acc;
  }, {});
  
  // Ordenar exercícios por order_index
  Object.keys(exercisesByDay || {}).forEach(day => {
    exercisesByDay[day].sort((a: any, b: any) => a.order_index - b.order_index);
  });

  return (
    <Card className="bg-white/90 backdrop-blur-lg border border-slate-100/50 shadow-sm hover:shadow-md transition-all overflow-hidden w-full dark:bg-gray-800/90 dark:border-gray-700/50">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start mb-1">
          <CardTitle className="text-xl text-slate-800 dark:text-white">{workout.title}</CardTitle>
          {workout.type && (
            <Badge variant="outline" className="bg-flame/10 text-flame border-flame/20 dark:bg-flame/20 dark:text-flame/90 dark:border-flame/30">
              {workout.type}
            </Badge>
          )}
        </div>
        <CardDescription className="line-clamp-2 dark:text-gray-300">
          {workout.description || "Sem descrição disponível"}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="pb-3">
        <div className="flex gap-3 text-sm text-muted-foreground dark:text-gray-400">
          {workout.days > 0 && (
            <div className="flex items-center gap-1">
              <span className="font-medium">{workout.days}</span> 
              {workout.days === 1 ? 'dia' : 'dias'}
            </div>
          )}
          {workout.difficulty_level && (
            <div className="flex items-center gap-1">
              <span className="font-medium">Nível:</span> 
              {workout.difficulty_level}
            </div>
          )}
        </div>

        {expanded && exercisesByDay && Object.keys(exercisesByDay).length > 0 && (
          <div className="mt-4 space-y-6">
            {Object.keys(exercisesByDay).map((day) => (
              <div key={day} className="border-t pt-4 dark:border-gray-700">
                <h4 className="text-base font-medium mb-3 pb-1 border-b border-slate-100 dark:border-gray-700 dark:text-white">
                  {t("newWorkout.days.title")} {day}
                </h4>
                <ul className="space-y-6">
                  {exercisesByDay[day].map((exercise: any) => (
                    <li key={exercise.id} className="flex flex-col gap-4">
                      <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-shrink-0 w-36 h-36 sm:w-48 sm:h-48 rounded-lg overflow-hidden bg-slate-100 dark:bg-gray-700 mx-auto sm:mx-0">
                          <ExerciseImage
                            imagePath={exercise.image_url || ""}
                            altText={exercise.name}
                            fallbackText={exercise.name}
                            className="w-full h-full object-contain p-2"
                            isCustomExercise={exercise.isCustom}
                          />
                        </div>
                        <div className="flex-1 space-y-2">
                          <h5 className="font-medium text-lg sm:text-xl dark:text-white">{exercise.name}</h5>
                          
                          <div className="flex flex-wrap gap-2 my-2">
                            {exercise.body_part && (
                              <Badge variant="outline" className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                {exercise.body_part}
                              </Badge>
                            )}
                            {exercise.target_muscle && (
                              <Badge variant="outline" className="bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                                {exercise.target_muscle}
                              </Badge>
                            )}
                          </div>
                          
                          <div className="bg-slate-50 dark:bg-gray-700/50 p-3 rounded-lg">
                            <div className="text-sm font-medium dark:text-white">
                              {isCardioExercise(exercise) ? (
                                <>
                                  {exercise.sets} {exercise.sets === 1 ? t("newWorkout.exercises.sets").slice(0, -1) : t("newWorkout.exercises.sets")} x {exercise.time || exercise.reps}
                                </>
                              ) : (
                                <>
                                  {exercise.sets} {exercise.sets === 1 ? t("newWorkout.exercises.sets").slice(0, -1) : t("newWorkout.exercises.sets")} x {exercise.reps} {exercise.weight ? `@ ${exercise.weight}` : ''}
                                </>
                              )}
                            </div>
                            
                            {exercise.notes && (
                              <div className="text-sm mt-2 text-muted-foreground dark:text-gray-300">
                                <span className="font-medium">Notas:</span> {exercise.notes}
                              </div>
                            )}
                          </div>
                          
                          {exercise.instructions && exercise.instructions.length > 0 && (
                            <div className="mt-2">
                              <details className="text-sm">
                                <summary className="font-medium cursor-pointer text-flame hover:text-flame/80 dark:text-flame/90">
                                  Instruções
                                </summary>
                                <div className="mt-2 pl-4 border-l-2 border-slate-200 dark:border-gray-600 space-y-1 text-muted-foreground dark:text-gray-300">
                                  {Array.isArray(exercise.instructions) ? (
                                    exercise.instructions.map((instruction: string, idx: number) => (
                                      <p key={idx}>{idx + 1}. {instruction}</p>
                                    ))
                                  ) : (
                                    <p>{exercise.instructions}</p>
                                  )}
                                </div>
                              </details>
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <CardFooter className="pt-0">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-center text-sm dark:text-gray-300 dark:hover:text-white"
        >
          {expanded ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1" /> Mostrar menos
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 mr-1" /> Ver exercícios
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};
